
import { Vue, Component } from 'vue-property-decorator'
import Column from '@/types/column'
import axios from 'axios'
import GuardCode from '@/types/guardCode'
import { notification } from 'ant-design-vue'
import { units } from '@/globalValues'
import Layout, { LayoutBudget } from '@/types/layout'
import AdditionalBudgetOverview from '@/components/budget/AdditionalBudgetOverview.vue'
import CopyLayoutBudgets from '@/views/layout/components/CopyLayoutBudgets.vue'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { CopyLayoutBudgets, AdditionalBudgetOverview }
})

export default class LayoutEditBudgetsTab extends Vue {
  settings = {
    routeNames: { list: 'layouts', show: 'layoutShow', edit: 'layoutEdit' },
    routeParamKey: 'layoutId',
    functionNames: { setCurrent: 'setCurrentLayout', clearCurrent: 'clearCurrentLayout', save: 'saveLayout' },
    getterNames: { current: 'currentLayout', loading: 'loadingLayout' }
  }

  columns:Column[] = [
    {
      title: 'Bewakingscode',
      key: 'guard_code',
      scopedSlots: { customRender: 'guard_code' }
    },
    {
      title: 'Omschrijving',
      key: 'description',
      scopedSlots: { customRender: 'description' }
    },
    {
      title: 'Aantal',
      key: 'qty',
      dataIndex: 'qty',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Eenheid',
      key: 'unit',
      scopedSlots: { customRender: 'unit' }
    },
    {
      title: 'Eenheidsprijs',
      key: 'unit_price',
      scopedSlots: { customRender: 'unit_price' }
    },
    {
      title: 'Totaalprijs',
      key: 'total_price',
      scopedSlots: { customRender: 'total_price' }
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  showModal = false
  searchTimer = 0
  loading = { search: false, saving: false }
  guardCodes:GuardCode[] = []
  layoutBudget:{ id?:number, layout_id:number, guard_code_id:number|null, description:string|null, qty:number, unit:string, unit_price:number, total_price:number }|null = null

  toggleModal (layoutBudget:LayoutBudget|null = null) : void {
    this.showModal = !this.showModal

    if (this.showModal && layoutBudget) {
      this.searchGuardCodes(layoutBudget.guard_code.code)
      this.layoutBudget = {
        id: layoutBudget.id,
        layout_id: parseInt(this.$route.params.layoutId),
        guard_code_id: layoutBudget.guard_code_id,
        description: layoutBudget.description,
        qty: layoutBudget.qty,
        unit: layoutBudget.unit,
        unit_price: layoutBudget.unit_price,
        total_price: layoutBudget.total_price
      }
    } else if (this.showModal && !layoutBudget) {
      this.layoutBudget = {
        layout_id: parseInt(this.$route.params.layoutId),
        guard_code_id: null,
        description: null,
        qty: 1,
        unit: 'PST',
        unit_price: 0,
        total_price: 0
      }
    } else {
      this.layoutBudget = null
      this.guardCodes = []
    }
  }

  searchGuardCodes (val:string) : void {
    if (val.length >= 3) {
      if (val.length >= 3) {
        clearTimeout(this.searchTimer)

        this.searchTimer = setTimeout(() => {
          this.loading.search = true
          axios.get(process.env.VUE_APP_API_URL + '/guardCodes?filter[search]=' + val).then((r) => {
            this.guardCodes = r.data.data
          }).finally(() => {
            this.loading.search = false
          })
        }, 500)
      }
    }
  }

  onGuardCodeChange (id:number) : void {
    if (this.layoutBudget) {
      this.layoutBudget.guard_code_id = id

      const defaultUnit = this.guardCodes.find((guardCode:GuardCode) => guardCode.id === id)
      this.layoutBudget.unit = defaultUnit && defaultUnit.default_unit ? defaultUnit.default_unit : 'PST'
    }
  }

  calculateTotal () : void {
    if (this.layoutBudget) {
      if (this.layoutBudget.unit === '%') {
        this.layoutBudget.total_price = (this.layoutBudget.qty / 100) * this.layoutBudget.unit_price
      } else {
        this.layoutBudget.total_price = this.layoutBudget.qty * this.layoutBudget.unit_price
      }
    }
  }

  addLayoutBudget () : void {
    this.loading.saving = true
    axios.post(process.env.VUE_APP_API_URL + '/layoutBudgets', this.layoutBudget).then(() => {
      this.toggleModal()
      this.$store.dispatch(this.settings.functionNames.setCurrent, this.$route.params[this.settings.routeParamKey])
      notification.success({ message: 'Budgetregel toegevoegd.', description: 'De budgetregel is succesvol opgeslagen.', duration: 3 })
    }).catch(() => {
      notification.error({ message: 'Budgetregel NIET toegevoegd.', description: 'Er is iets mis gegaan. Probeer het later nog eens. ', duration: 0 })
    }).finally(() => {
      this.loading.saving = false
    })
  }

  updateLayoutBudget () : void {
    this.loading.saving = true
    if (this.layoutBudget && this.layoutBudget.id) {
      axios.patch(process.env.VUE_APP_API_URL + '/layoutBudgets/' + this.layoutBudget.id, this.layoutBudget).then(() => {
        this.toggleModal()
        this.$store.dispatch(this.settings.functionNames.setCurrent, this.$route.params[this.settings.routeParamKey])
        notification.success({ message: 'Budgetregel bijgewerkt.', description: 'De budgetregel is succesvol bijgewerkt.', duration: 3 })
      }).catch(() => {
        notification.error({ message: 'Budgetregel NIET bijgewerkt.', description: 'Er is iets mis gegaan. Probeer het later nog eens. ', duration: 0 })
      }).finally(() => {
        this.loading.saving = false
      })
    }
  }

  deleteBudgetFromLayout (id:number) : void {
    axios.delete(process.env.VUE_APP_API_URL + '/layoutBudgets/' + id).then(() => {
      this.$store.dispatch(this.settings.functionNames.setCurrent, this.$route.params[this.settings.routeParamKey])
      notification.success({ message: 'Budgetregel verwijderd.', description: 'De budgetregel is succesvol verwijderd.', duration: 3 })
    }).catch(() => {
      notification.error({ message: 'Budgetregel NIET verwijderd.', description: 'Er is iets mis gegaan. Probeer het later nog eens. ', duration: 0 })
    })
  }

  budgetsCopied () : void {
    this.$store.dispatch(this.settings.functionNames.setCurrent, this.$route.params[this.settings.routeParamKey])
  }

  filterDropdown (input:string, option:{componentOptions:{children:{text:string}[]}}) : boolean {
    return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  get currentItem () : Layout {
    return this.$store.getters[this.settings.getterNames.current]
  }

  get editMode () : boolean {
    return !!(this.layoutBudget && this.layoutBudget.id)
  }

  get units () : { name:string, value:string }[] {
    return units
  }
}
